import { useEffect, useState } from "react";
import api from "../../lib/apiClient";

/**
 * Profile-page playback side-effects extracted from ProfilePage.jsx
 * (Jun 8, 2026) to keep that file under the 600-line product rule.
 *
 * Two behaviours bundled — both already fire on the SAME deps
 * (isPlaying, currentVideoIndex, playableVideos):
 *   1. Hydrate the comments list for the currently-playing video.
 *   2. Hydrate the "nearby videos" list (geo-radius ~200km).
 *
 * Also tracks viewed video IDs (for FirstTimeBookmarkTooltip's
 * "after 3 views" gate).
 *
 * Returns the state the caller renders; mutators are kept exposed
 * for the comment-post handler that needs to prepend to `comments`.
 */
export function useProfileVideoSync({ isPlaying, currentVideoIndex, playableVideos }) {
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [nearbyVideos, setNearbyVideos] = useState([]);
  const [viewedVideoIds, setViewedVideoIds] = useState(() => new Set());

  useEffect(() => {
    const fetchComments = async () => {
      if (isPlaying && playableVideos[currentVideoIndex]) {
        const vid = playableVideos[currentVideoIndex].id;
        setViewedVideoIds(prev => prev.has(vid) ? prev : new Set(prev).add(vid));
        setLoadingComments(true);
        try {
          const res = await api.get(`/videos/${vid}/comments`);
          setComments(res.data);
        } catch (err) {
          console.error(err);
          setComments([]);
        }
        setLoadingComments(false);
      }
    };
    fetchComments();
  }, [isPlaying, currentVideoIndex, playableVideos]);

  useEffect(() => {
    const fetchNearbyVideos = async () => {
      const currentVideo = playableVideos[currentVideoIndex];
      if (isPlaying && currentVideo?.location?.lat && currentVideo?.location?.lng) {
        try {
          const res = await api.get(`/videos/map`);
          const nearby = res.data
            .filter(v => {
              if (!v.location?.lat || !v.location?.lng) return false;
              if (v.id === currentVideo.id) return false;
              const distance = Math.sqrt(
                Math.pow(v.location.lat - currentVideo.location.lat, 2) +
                Math.pow(v.location.lng - currentVideo.location.lng, 2)
              );
              return distance < 2;
            })
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .slice(0, 10);
          setNearbyVideos(nearby);
        } catch (err) {
          console.error("Failed to fetch nearby videos:", err);
          setNearbyVideos([]);
        }
      }
    };
    fetchNearbyVideos();
  }, [isPlaying, currentVideoIndex, playableVideos]);

  return {
    comments, setComments,
    loadingComments,
    nearbyVideos,
    viewedVideoIds,
  };
}
